import { IPublicClientApplication, InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { createTheme, CssBaseline, ThemeProvider, styled, Stack } from "@mui/material";
import { ConfirmProvider } from "hooks/ConfirmationDialog";
import { MuiSnackbar } from "MuiSnackbar";
import React, { ReactNode, useMemo } from "react";
import { Outlet, RouterProvider } from "react-router-dom";
import { ApiProvider } from "services/hooks";
import { GoogleAnalytics } from "services/hooks/google-analytics";
import { SUBSCRIPTION_KEY, swrLoginRequest } from "./authConfig";
import TopNavigation from "./components/TopNavigation/TopNavigation";
import { useMicrosoftInterceptor } from "@sanmanuel/microsoft-auth";
import {
  SMApiProvider,
  defaultHeadersInterceptor,
  customErrorInterceptor,
  useSWRFetcher,
  combineRequestInterceptors,
} from "@sanmanuel/swr";
import { SWRConfig } from "swr";
import { ApiError } from "services/api/misc";
import "./App.css";
import { AllowedAccess, PermissionProvider } from "@sanmanuel/permissions";
import { usePermissions } from "services/api/SWRService";
import { createAppRouter } from "AppRoutes";
import { getThemeOptions } from "AppTheme";
import { usePaletteMode, useStateAndStore } from "@sanmanuel/smkit-react";
import { LicenseInfo } from "@mui/x-license-pro";
import { Loading } from "pages/LoadingPage";
import { ErrorPage } from "pages/ErrorPage";
import { UnauthorizedPage } from "pages/Unauthorized/UnauthorizedPage";

type AppProps = {
  msalInstance: IPublicClientApplication;
};

LicenseInfo.setLicenseKey(
  "2109afdf855769bcb68d4ea023a2b899Tz05MDQxMixFPTE3NDc0MzUzMDQwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI="
);

export const Offset = styled("div")(({ theme }) => theme.mixins.toolbar);

function App({ msalInstance }: AppProps) {
  const [mocking, setMocking] = useStateAndStore<boolean>("mocking", false);

  const [mode, toggleMode] = usePaletteMode(true, "dark");

  // Update the theme only if the mode changes
  const theme = React.useMemo(() => {
    return createTheme(getThemeOptions(mode.value));
  }, [mode]);

  const router = createAppRouter(
    <Stack height="100%">
      <GoogleAnalytics />
      <TopNavigation mode={mode} onPaletteModeChanged={toggleMode} mocking={mocking} onMockingChanged={setMocking} />
      <Offset />
      <MuiSnackbar>
        <Outlet />
      </MuiSnackbar>
    </Stack>
  );

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={swrLoginRequest}>
        <AppAuth isMocking={mocking}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <ConfirmProvider>
              <ApiProvider mock={mocking}>
                <RouterProvider router={router} />
              </ApiProvider>
            </ConfirmProvider>
          </ThemeProvider>
        </AppAuth>
      </MsalAuthenticationTemplate>
    </MsalProvider>
  );
}

type AppAuthProps = {
  isMocking: boolean;
  children?: ReactNode | ReactNode[];
};

export const AppAuth = ({ isMocking, children }: AppAuthProps) => {
  const microsoftInterceptor = useMicrosoftInterceptor(swrLoginRequest);
  const interceptors = useMemo(
    () => ({
      request: combineRequestInterceptors([
        defaultHeadersInterceptor({
          "Ocp-Apim-Subscription-Key": SUBSCRIPTION_KEY,
        }),
        microsoftInterceptor,
      ]),
      response: customErrorInterceptor(async (r) => new ApiError(await r.json())),
    }),
    [microsoftInterceptor]
  );

  return (
    <SMApiProvider isMocking={isMocking} interceptors={interceptors}>
      <AppAuthInner>
        <AppPermissions>{children}</AppPermissions>
      </AppAuthInner>
    </SMApiProvider>
  );
};

export const AppAuthInner = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const baseFetcher = useSWRFetcher();
  return <SWRConfig value={{ fetcher: baseFetcher }}>{children}</SWRConfig>;
};

const AppPermissions = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const { data: permissions, isLoading, error } = usePermissions();
  if (isLoading) return <Loading />;
  if (!permissions || error) return <ErrorPage errorMessage="Unable to access permissions" />;

  return (
    <PermissionProvider permissions={permissions}>
      <AllowedAccess fallback={<UnauthorizedPage />}>{children}</AllowedAccess>
    </PermissionProvider>
  );
};

export default App;
